import React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import Grid from "@mui/joy/Grid";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmDialog from "../../../components/confirmDialog/ConfirmDialog";
import { NotificationService } from "../../../common/notification";
import { formatUtcDateTime } from "../../../common/dateTime";
import { Game } from "../../../models/games";
import { AdminExternalProfitRuleInfo, AdminExternalProviderInfo } from "../../../models/externalMarkets";
import { formatMoneyKopecks, toNum } from "../../bids/analytics/format";
import { extProfitRuleDeleteApi, extProfitRuleSaveApi, extProfitRulesApi } from "../externalMarketsApi";
import {
    formatPercent,
    kopecksToRublesInput,
    parseOptionalNumber,
    resolveGameName,
    rublesToKopecksOptional,
} from "../components/shared";

type Props = {
    provider: AdminExternalProviderInfo;
    games: Game[];
};

/** 0 — общие правила поставщика для всех игр */
const ALL_GAMES = 0;

export function ProfitRulesTab(props: Props) {
    const { provider, games } = props;
    const [scope, setScope] = React.useState<number>(ALL_GAMES);
    const [rules, setRules] = React.useState<AdminExternalProfitRuleInfo[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [editing, setEditing] = React.useState<AdminExternalProfitRuleInfo | "new" | null>(null);
    const [deleting, setDeleting] = React.useState<AdminExternalProfitRuleInfo | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await extProfitRulesApi(provider.key, scope);
            if (res.result) {
                setRules(res.value ?? []);
            } else {
                setRules([]);
                NotificationService.alertError(res.error ?? "Не удалось загрузить правила прибыли");
            }
        } finally {
            setLoading(false);
        }
    }, [provider.key, scope]);

    React.useEffect(() => {
        load().catch(console.error);
    }, [load]);

    return (
        <Stack spacing={1.5}>
            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                    p: 2,
                    bgcolor: "background.level1",
                    borderRadius: "md",
                }}
            >
                <FormControl size="sm" sx={{ minWidth: 220 }}>
                    <FormLabel>Правила для</FormLabel>
                    <Select size="sm" value={scope} onChange={(_, v) => setScope(typeof v === "number" ? v : ALL_GAMES)}>
                        <Option value={ALL_GAMES}>Все игры поставщика</Option>
                        {provider.markets.map((m) => (
                            <Option key={m.gameId} value={m.gameId}>
                                {resolveGameName(games, m.gameId)}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <Typography level="body-xs" sx={{ flex: 1, minWidth: 260 }}>
                    Диапазон — по себестоимости у поставщика в рублях: (от; до]. Правило игры приоритетнее общего.
                    Если заданы и фикс, и процент — берётся большее, затем не меньше минимальной прибыли.
                    Изменение применяется к витрине при следующей синхронизации.
                </Typography>
                <Button size="sm" startDecorator={<AddIcon />} onClick={() => setEditing("new")}>
                    Добавить правило
                </Button>
            </Box>

            {loading ? (
                <CircularProgress size="sm" />
            ) : rules.length === 0 ? (
                <Typography level="body-sm">
                    Правил нет. Предметы без подходящего правила не выставляются на витрину.
                </Typography>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "auto" }}>
                    <Table size="sm" hoverRow>
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>#</th>
                                <th>Себестоимость от</th>
                                <th>до</th>
                                <th>Фикс</th>
                                <th>Процент</th>
                                <th>Мин. прибыль</th>
                                <th>Изменено</th>
                                <th style={{ width: 90 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.id}</td>
                                    <td>{formatMoneyKopecks(r.minAmountExclusive)}</td>
                                    <td>{r.maxAmountInclusive != null ? formatMoneyKopecks(r.maxAmountInclusive) : "∞"}</td>
                                    <td>{r.fixedRate != null ? formatMoneyKopecks(r.fixedRate) : "—"}</td>
                                    <td>{r.percentRate != null ? formatPercent(r.percentRate) : "—"}</td>
                                    <td>{r.minProfit != null ? formatMoneyKopecks(r.minProfit) : "—"}</td>
                                    <td>
                                        <Typography level="body-xs">{formatUtcDateTime(r.updated)}</Typography>
                                        <Typography level="body-xs">{r.updatedBy ?? "—"}</Typography>
                                    </td>
                                    <td>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Изменить">
                                                <IconButton size="sm" variant="outlined" onClick={() => setEditing(r)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Удалить">
                                                <IconButton size="sm" variant="outlined" color="danger" onClick={() => setDeleting(r)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            <RuleDialog
                rule={editing}
                title={`${provider.name}: ${scope === ALL_GAMES ? "все игры" : resolveGameName(games, scope)}`}
                onClose={() => setEditing(null)}
                onSave={async (rule) => {
                    const res = await extProfitRuleSaveApi(provider.key, scope, rule);
                    if (res.result) {
                        NotificationService.alertSuccess("Правило сохранено");
                        setEditing(null);
                        await load();
                    } else {
                        NotificationService.alertError(res.error ?? "Не удалось сохранить правило");
                    }
                }}
            />

            <ConfirmDialog
                title="Удаление правила"
                open={deleting !== null}
                setOpen={(open) => !open && setDeleting(null)}
                onConfirm={async () => {
                    if (!deleting) {
                        return;
                    }
                    const res = await extProfitRuleDeleteApi(provider.key, scope, deleting.id);
                    if (res.result) {
                        NotificationService.alertSuccess("Правило удалено");
                        await load();
                    } else {
                        NotificationService.alertError(res.error ?? "Не удалось удалить правило");
                    }
                }}
            >
                {`Удалить правило #${deleting?.id ?? ""}? Предметы из его диапазона перестанут выставляться, если нет общего правила.`}
            </ConfirmDialog>
        </Stack>
    );
}

type RuleForm = {
    min: string;
    max: string;
    fixed: string;
    percent: string;
    minProfit: string;
};

/** Прибыль для себестоимости по правилу — как на сервере, для подсказки в форме */
export function previewProfit(costKopecks: number, fixed?: number, percent?: number, minProfit?: number): number | undefined {
    const byFixed = fixed;
    const byPercent = percent !== undefined ? Math.round((costKopecks * percent) / 100) : undefined;
    if (byFixed === undefined && byPercent === undefined) {
        return undefined;
    }
    const profit = Math.max(byFixed ?? 0, byPercent ?? 0);
    return minProfit !== undefined ? Math.max(profit, minProfit) : profit;
}

function RuleDialog(props: {
    rule: AdminExternalProfitRuleInfo | "new" | null;
    title: string;
    onClose: () => void;
    onSave: (rule: {
        id?: number;
        minAmountExclusive: number;
        maxAmountInclusive?: number;
        fixedRate?: number;
        percentRate?: number;
        minProfit?: number;
    }) => Promise<void>;
}) {
    const { rule, title, onClose, onSave } = props;
    const [form, setForm] = React.useState<RuleForm>({ min: "0", max: "", fixed: "", percent: "", minProfit: "" });
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (rule === "new") {
            setForm({ min: "0", max: "", fixed: "", percent: "", minProfit: "" });
        } else if (rule) {
            setForm({
                min: kopecksToRublesInput(rule.minAmountExclusive),
                max: kopecksToRublesInput(rule.maxAmountInclusive),
                fixed: kopecksToRublesInput(rule.fixedRate),
                percent: rule.percentRate != null ? String(toNum(rule.percentRate)) : "",
                minProfit: kopecksToRublesInput(rule.minProfit),
            });
        }
    }, [rule]);

    const min = rublesToKopecksOptional(form.min) ?? 0;
    const max = rublesToKopecksOptional(form.max);
    const fixed = rublesToKopecksOptional(form.fixed);
    const percent = parseOptionalNumber(form.percent);
    const minProfit = rublesToKopecksOptional(form.minProfit);

    const errors: string[] = [];
    if (min < 0) errors.push("Нижняя граница не может быть отрицательной");
    if (max !== undefined && max <= min) errors.push("Верхняя граница должна быть больше нижней");
    if (fixed === undefined && percent === undefined) errors.push("Задайте фиксированную прибыль или процент");
    if ((fixed ?? 0) < 0 || (minProfit ?? 0) < 0) errors.push("Прибыль не может быть отрицательной");
    if (percent !== undefined && (percent < 0 || percent > 1000)) errors.push("Процент должен быть от 0 до 1000");

    const sampleCost = max !== undefined ? max : Math.max(min + 100_00, 100_00);
    const sample = previewProfit(sampleCost, fixed, percent, minProfit);

    const field = (key: keyof RuleForm, label: string, hint?: string) => (
        <FormControl size="sm">
            <FormLabel>{label}</FormLabel>
            <Input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
            {hint && <FormHelperText>{hint}</FormHelperText>}
        </FormControl>
    );

    return (
        <Modal open={rule !== null} onClose={() => !saving && onClose()}>
            <ModalDialog sx={{ width: { xs: "100%", sm: 560 } }}>
                <ModalClose disabled={saving} />
                <Typography level="h4">{rule === "new" ? "Новое правило" : `Правило #${(rule as AdminExternalProfitRuleInfo)?.id ?? ""}`}</Typography>
                <Typography level="body-sm">{title}</Typography>
                <Grid container spacing={1.5}>
                    <Grid xs={6}>{field("min", "Себестоимость от, ₽", "Не включительно")}</Grid>
                    <Grid xs={6}>{field("max", "до, ₽", "Включительно; пусто — без ограничения")}</Grid>
                    <Grid xs={4}>{field("fixed", "Фикс, ₽")}</Grid>
                    <Grid xs={4}>{field("percent", "Процент, %")}</Grid>
                    <Grid xs={4}>{field("minProfit", "Мин. прибыль, ₽")}</Grid>
                </Grid>
                {sample !== undefined && errors.length === 0 && (
                    <Typography level="body-xs">
                        Пример: себестоимость {formatMoneyKopecks(sampleCost)} → прибыль {formatMoneyKopecks(sample)}, цена на
                        витрине {formatMoneyKopecks(sampleCost + sample)}
                    </Typography>
                )}
                {errors.map((e) => (
                    <Typography key={e} level="body-xs" color="danger">
                        {e}
                    </Typography>
                ))}
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button variant="plain" color="neutral" disabled={saving} onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        loading={saving}
                        disabled={errors.length > 0}
                        onClick={async () => {
                            setSaving(true);
                            try {
                                await onSave({
                                    id: rule !== "new" && rule ? rule.id : undefined,
                                    minAmountExclusive: min,
                                    maxAmountInclusive: max,
                                    fixedRate: fixed,
                                    percentRate: percent,
                                    minProfit,
                                });
                            } finally {
                                setSaving(false);
                            }
                        }}
                    >
                        Сохранить
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}
